import Calendar from '@/components/calendar/Calendar';
import { ReactDayPicker2 } from '@/components/calendar/ReactDayPicker2';
import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';
import { DATE_YMD_2, DAY_OF_WEEK, ekDomain, NON_RENTAL_TYPES, NUMBER_VALIDATE_REGEX, ROUTES, SUBMIT_MOMENT_DATE_YMD } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { NonRentalDateInfo } from '@/types/bike';
import { FormType } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { cn, isDeleted } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { addMonths } from 'date-fns';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { NavProps } from 'react-day-picker';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface NonRentalDateFormProps {
    bikeIds: number[],
    isPage: boolean,
    type?: FormType
}

export default observer(function NonRentalDateForm({ bikeIds, isPage, type }: NonRentalDateFormProps) {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // store
    const {
        modalStore: { hideAllModals, hideModal, showAlertModal },
        bikeStore: { bikeDetail, getBikeDetail, createNonRentalDate, updateNonRentalDate, resetNonRentalDate, getNonRentalDateSetting, setObservable },
        uiStore: { devicesScreen: { mobile, desktop } }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        nonRentalType: yup
            .string(),

        // date
        nonRentalDate: yup.array().of(
            yup.date()
        ),

        // weekly
        selectedDaysOfWeekly: yup
            .object(
                Object.keys(DAY_OF_WEEK).reduce((acc, key) => {
                    acc[DAY_OF_WEEK[key].key] = yup.boolean();
                    return acc;
                }, {})
            ),
        startTimeOfMonth: yup.lazy((value) => {
            if (value === '') {
                return yup
                    .string()
                    .optional();
            }

            return yup
                .string()
                .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
                .test(
                    'min-value',
                    t('form_error_validate.min', { min: 1 }),
                    (val) => {
                        if (val == null || val === '') {
                            return true;
                        }
                        const num = Number(val);
                        return num >= 1;
                    }
                )
                .test(
                    'max-value',
                    t('form_error_validate.max', { max: 31 }),
                    (val) => {
                        if (val == null || val === '') {
                            return true;
                        }
                        const num = Number(val);
                        return num <= 31;
                    }
                )
                .test(
                    'startTime-less-than-endTime',
                    t('form_error_validate.start_date_must_be_before_than_end_date'),
                    function (value) {
                        const endTime = this.parent.endTimeOfMonth;
                        if (endTime && value && NUMBER_VALIDATE_REGEX.test(endTime)) return Number(value) <= Number(endTime);
                        return true;
                    }
                );
        }),

        // endTimeOfMonth
        endTimeOfMonth: yup.lazy((value) => {
            if (value === '') {
                return yup
                    .string()
                    .optional();
            }

            return yup
                .string()
                .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
                .test(
                    'min-value',
                    t('form_error_validate.min', { min: 1 }),
                    (val) => {
                        if (val == null || val === '') {
                            return true;
                        }
                        const num = Number(val);
                        return num >= 1;
                    }
                )
                .test(
                    'max-value',
                    t('form_error_validate.max', { max: 31 }),
                    (val) => {
                        if (val == null || val === '') {
                            return true;
                        }
                        const num = Number(val);
                        return num <= 31;
                    }
                )
                .test(
                    'endTime-greater-than-startTime',
                    t('form_error_validate.end_date_must_be_later_than_start_date'),
                    function (value) {
                        const startTime = this.parent.startTimeOfMonth;
                        if (startTime && value && NUMBER_VALIDATE_REGEX.test(startTime)) return Number(value) >= Number(startTime);
                        return true;
                    }
                );
        }),
        // startDate
        startDate: yup
            .date()
            .nullable()
            .when('nonRentalType', {
                is: type => type !== NON_RENTAL_TYPES.Day,
                then: schema =>
                    schema
                        .required('form_error_validate.required')
                        .test(
                            'startDate-less-than-endDate',
                            t('form_error_validate.invalid_time_range'),
                            function (value) {
                                const endDate = this.parent.endDate;
                                if (!value || !endDate) return true;
                                return value <= endDate;
                            }
                        ),
                otherwise: schema => schema.nullable()
            }),

        // endDate
        endDate: yup
            .date()
            .nullable()
            .when('nonRentalType', {
                is: type => type !== NON_RENTAL_TYPES.Day,
                then: schema =>
                    schema
                        .required('form_error_validate.required')
                        .test(
                            'endDate-greater-than-startDate',
                            t('form_error_validate.invalid_time_range'),
                            function (value) {
                                const startDate = this.parent.startDate;
                                if (!value || !startDate) return true;
                                return value >= startDate;
                            }
                        ),
                otherwise: schema => schema.nullable()
            })
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const defaultValues = {
        nonRentalType: NON_RENTAL_TYPES.Day,
        nonRentalDate: [],
        selectedDaysOfWeekly: {
            sunday: false,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false
        },
        startTimeOfMonth: '',
        endTimeOfMonth: '',
        startDate: null,
        endDate: null
    };

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: defaultValues
    });

    const watchNonRentalType = form.watch('nonRentalType');
    const values = form.watch();
    const [fromMonth, setFromMonth] = useState(new Date());
    const toMonth = addMonths(fromMonth, 1);

    const isOnlyView = false;
    // const [isOnlyView, setOnlyView] = useState<boolean>(false);

    const isDeletedDisable = useMemo((): boolean => {
        return isDeleted(Number(bikeDetail?.deleteStatus));
    }, [bikeDetail?.deleteStatus]);

    // create description
    const description = useMemo(() => {
        const { nonRentalType, selectedDaysOfWeekly, startTimeOfMonth, endTimeOfMonth, startDate, endDate } = values;

        // day
        if (nonRentalType === NON_RENTAL_TYPES.Day && values.nonRentalDate && values.nonRentalDate.length > 0) {
            const formattedDates = values.nonRentalDate
                .map(date => formatDateTime(date, DATE_YMD_2))
                .join(', ');

            return t('sentences.non_rental_day', {
                dates: formattedDates
            });
        }

        // weekly
        if (nonRentalType === NON_RENTAL_TYPES.Weekly && values.startDate && values.endDate) {
            const selectedDays = Object.entries(DAY_OF_WEEK)
                .filter(([_, { key }]) => selectedDaysOfWeekly?.[key])
                .map(([_, { key }]) => t(`words_title.${key}`))
                .join('、');

            if (!selectedDays) return '';

            return t('sentences.non_rental_weekly', {
                days: selectedDays,
                startDate: formatDateTime(startDate, DATE_YMD_2),
                endDate: formatDateTime(endDate, DATE_YMD_2)
            });
        }

        // monthly
        if (nonRentalType === NON_RENTAL_TYPES.Monthly && values.startDate && values.endDate && startTimeOfMonth && endTimeOfMonth) {
            return t('sentences.non_rental_monthly', {
                startTimeOfMonth,
                endTimeOfMonth,
                startDate: formatDateTime(startDate, DATE_YMD_2),
                endDate: formatDateTime(endDate, DATE_YMD_2)
            });
        }

        return '';
    }, [values]);

    // lifecycle
    useEffect(() => {
        fetchData();

        return () => {
            if (!isPage) {
                setObservable('bikeDetail', null, { isMergeObject: false });
            };
        };
    }, []);

    useEffect(() => {
        if (bikeDetail?.id && watchNonRentalType) {
            let payload = {
                id: bikeDetail.id,
                nonRentalType: watchNonRentalType
            };
            fetchDataNonRental(payload);
        }
    }, [bikeDetail, watchNonRentalType]);

    // function
    const handleClickCancel = () => {
        if (isPage) {
            navigate(`${isPathEk(pathname) ? `/${ekDomain}/${ROUTES.bikeManagement.hrefEK}` : ROUTES.bikeManagement.href}`);
        } else {
            hideAllModals();
        }
    };

    const fetchData = async () => {
        if (bikeIds.length === 1) {
            getBikeDetail(bikeIds[0]);
        }
    };

    const fetchDataNonRental = async (payload: any) => {
        const res = await flowResult(getNonRentalDateSetting(payload));
        if (res) {
            handleSetDataForForm(res, payload.nonRentalType);
        }
    };

    const handleSetDataForForm = (nonRentalDateInfor: NonRentalDateInfo, nonRentalType: string) => {
        const { nonRentalDates, nonRentalDatesDetails = [] } = nonRentalDateInfor;

        let dateReset = {
            nonRentalType
        };
        switch (nonRentalType) {
            case NON_RENTAL_TYPES.Day:
                form.reset({
                    ...dateReset,
                    nonRentalDate: nonRentalDatesDetails ?? []
                });
                break;
            case NON_RENTAL_TYPES.Weekly:
                form.reset({
                    ...dateReset,
                    ...{
                        selectedDaysOfWeekly: {
                            sunday: nonRentalDates?.sunday ?? false,
                            monday: nonRentalDates?.monday ?? false,
                            tuesday: nonRentalDates?.tuesday ?? false,
                            wednesday: nonRentalDates?.wednesday ?? false,
                            thursday: nonRentalDates?.thursday ?? false,
                            friday: nonRentalDates?.friday ?? false,
                            saturday: nonRentalDates?.saturday ?? false
                        },
                        startDate: nonRentalDates?.startDate ? new Date(nonRentalDates?.startDate) : null,
                        endDate: nonRentalDates?.endDate ? new Date(nonRentalDates?.endDate) : null
                    }
                });
                break;
            case NON_RENTAL_TYPES.Monthly:
                form.reset({
                    ...dateReset,
                    ...{
                        startTimeOfMonth: nonRentalDates?.startTimeOfMonth?.toString() ?? '',
                        endTimeOfMonth: nonRentalDates?.endTimeOfMonth?.toString() ?? '',
                        startDate: nonRentalDates?.startDate ? new Date(nonRentalDates?.startDate) : null,
                        endDate: nonRentalDates?.endDate ? new Date(nonRentalDates?.endDate) : null
                    }

                });
                break;
        }
    };

    const onSubmit = async (data: FormData) => {
        const nonRentalDate = {
            nonRentalType: data.nonRentalType,
            weekly: {
                ...data.selectedDaysOfWeekly,
                startDate: formatDateTime(data.startDate, SUBMIT_MOMENT_DATE_YMD),
                endDate: formatDateTime(data.endDate, SUBMIT_MOMENT_DATE_YMD)
            },
            monthly: {
                startTimeOfMonth: data.startTimeOfMonth,
                endTimeOfMonth: data.endTimeOfMonth,
                startDate: formatDateTime(data.startDate, SUBMIT_MOMENT_DATE_YMD),
                endDate: formatDateTime(data.endDate, SUBMIT_MOMENT_DATE_YMD)
            },
            day: { nonRentalDate: data.nonRentalDate?.map(date => formatDateTime(date, SUBMIT_MOMENT_DATE_YMD)) }
        };

        if (isPage) {
            const res = await flowResult(createNonRentalDate({ id: bikeIds[0], nonRentalDate }));
            if (res) {
                showAlertModal({
                    id: 'update-success',
                    type: 'success',
                    content: t('messages.update_success')
                });
            }
        } else {
            const res = await flowResult(updateNonRentalDate({ bikeIds, nonRentalDate }));
            if (res) {
                hideAllModals();
                showAlertModal({
                    id: 'update-success',
                    type: 'success',
                    content: t('messages.update_success')
                });
            }
        }
    };

    const handleNextMonth = () => {
        setFromMonth(prev => addMonths(prev, 1));
    };

    const resetNonRetalDate = async () => {
        if (bikeIds && bikeIds?.length > 0) {
            const bikeId = bikeIds?.[0];
            if (!bikeId) {
                return;
            }

            const idModal = 'reset-non-retal-date';
            showAlertModal({
                id: idModal,
                isConfirmModal: true,
                content: '本当に解除して良いですか？',
                cancelButton: t('button.cancel'),
                saveButton: t('button.reset_non_rental_date'),
                onSave: async () => {
                    hideModal(idModal);
                    const res = await flowResult(resetNonRentalDate({ bikeId, nonRentalType: values.nonRentalType }));
                    if (res) {
                        await flowResult(getBikeDetail(Number(bikeId)));
                    }
                }
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={classNames(!isPage && 'max-h-[78vh] overflow-y-auto')}>
                <div className={classNames('flex flex-col bg-white layout-card', !isPage ? 'p-0 gap-2' : 'gap-4 mb-4')}>
                    <FormField
                        control={form.control}
                        name='nonRentalType'
                        render={({ field }) => (
                            <FormItem>
                                <Label className='font-semibold'>{t('words_title.setting_by')}</Label>
                                <FormControl>
                                    <RadioGroup
                                        disabled={isOnlyView}
                                        className='flex point-375:flex-row flex-col md:gap-10 sm:gap-6 gap-2 mt-2 flex-wrap'
                                        value={field.value}
                                        onValueChange={(value) => {
                                            field.onChange(value);
                                        }}
                                    >
                                        {Object.values(NON_RENTAL_TYPES).map((type, index) => {
                                            return (
                                                <div key={index} className='flex items-center space-x-2'>
                                                    <RadioGroupItem value={type} id={type} />
                                                    <Label className={classNames(!isOnlyView && 'cursor-pointer', 'font-normal')} htmlFor={type}>{t(`words_title.${type}`)}</Label>
                                                </div>
                                            );
                                        })}
                                    </RadioGroup>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <hr />
                    {watchNonRentalType === NON_RENTAL_TYPES.Day ?
                        (
                            <FormField
                                control={form.control}
                                name='nonRentalDate'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <div className='w-full flex gap-1'>
                                                <div className='flex-1'>
                                                    <ReactDayPicker2
                                                        mode='multiple'
                                                        className='max-w-full border-none pt-2'
                                                        showOutsideDays={false}
                                                        selected={field.value?.filter((date): date is Date => date !== undefined)}
                                                        onSelect={field.onChange}
                                                        month={fromMonth}
                                                        onMonthChange={setFromMonth}
                                                        disabled={() => {
                                                            return isOnlyView || isDeletedDisable;
                                                        }}
                                                        captionLayout='label'
                                                        components={{
                                                            Nav: ({ children, className, ...props }: NavProps) => {
                                                                const [previousButton, nextButton] = React.Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;

                                                                return (
                                                                    <nav className={cn('flex items-center justify-between', className)} {...props}>
                                                                        {previousButton}
                                                                        {(!isPage || mobile) && (
                                                                            <div>
                                                                                {nextButton}
                                                                            </div>
                                                                        )}

                                                                    </nav>
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                {isPage && desktop && (
                                                    <div className='flex-1'>
                                                        <ReactDayPicker2
                                                            mode='multiple'
                                                            className='max-w-full border-none pt-2'
                                                            showOutsideDays={false}
                                                            selected={field.value?.filter((date): date is Date => date !== undefined)}
                                                            onSelect={field.onChange}
                                                            month={toMonth}
                                                            onMonthChange={handleNextMonth}
                                                            disabled={() => {
                                                                return isOnlyView || isDeletedDisable;
                                                            }}
                                                            captionLayout='label'
                                                            components={{
                                                                Nav: ({ children, className, ...props }: NavProps) => {
                                                                    const [, nextButton] = React.Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;

                                                                    return (
                                                                        <nav className={cn('flex items-center justify-between', className)} {...props}>
                                                                            <div></div>
                                                                            <div>{nextButton}</div>
                                                                        </nav>
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        ) :
                        (
                            <div className='flex flex-col gap-4 w-full max-w-[700px]'>
                                <Label className='font-semibold'>{t('field.set_recurrence')}</Label>
                                <FormField
                                    control={form.control}
                                    name='startDate'
                                    render={({ field, fieldState }) => (
                                        <FormItem>
                                            <FormLabel required>{t('field.start')}</FormLabel>
                                            <FormControl>
                                                <Calendar
                                                    disabled={(date) => {
                                                        return ((values.endDate && date.getTime() > (values.endDate?.getTime())) || isOnlyView);
                                                    }}
                                                    calendarDisabled={isDeletedDisable}
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={date => field.onChange(date || null)}
                                                    useIconRemove={true}
                                                    required={false}
                                                    isInvalid={!!fieldState.error}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='endDate'
                                    render={({ field, fieldState }) => (
                                        <FormItem>
                                            <FormLabel required>{t('field.end')}</FormLabel>
                                            <FormControl>
                                                <Calendar
                                                    disabled={(date) => {
                                                        return ((values.startDate && date.getTime() < (values.startDate?.getTime())) || isOnlyView);
                                                    }}
                                                    calendarDisabled={isDeletedDisable}
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={date => field.onChange(date || null)}
                                                    useIconRemove={true}
                                                    required={false}
                                                    isInvalid={!!fieldState.error}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {watchNonRentalType === NON_RENTAL_TYPES.Weekly && (
                                    <FormField
                                        control={form.control}
                                        name='selectedDaysOfWeekly'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t('field.repeat_every_week')}</FormLabel>
                                                <div className='flex gap-4 items-center flex-wrap'>
                                                    {Object.values(DAY_OF_WEEK).map(({ shortName, key }, index) => (
                                                        <FormControl key={key}>
                                                            <div className='flex items-center'>
                                                                <Checkbox
                                                                    disabled={isOnlyView || isDeletedDisable}
                                                                    id={`selectedDaysOfWeekly-${index}`}
                                                                    checked={field.value?.[key]}
                                                                    onCheckedChange={(checked) => {
                                                                        field.onChange({
                                                                            ...field.value,
                                                                            [key]: checked
                                                                        });
                                                                    }}
                                                                />
                                                                <Label
                                                                    className='ml-2 cursor-pointer font-normal'
                                                                    htmlFor={`selectedDaysOfWeekly-${index}`}
                                                                >
                                                                    {t(`day.short_name.${shortName}`)}
                                                                </Label>
                                                            </div>
                                                        </FormControl>
                                                    ))}
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                )}
                                {watchNonRentalType === NON_RENTAL_TYPES.Monthly && (
                                    <div>
                                        <FormLabel>{t('field.repeat_every_month')}</FormLabel>
                                        <div className='flex xsm:flex-row flex-col xsm:gap-6 gap-2 items-center mb-2'>
                                            <FormField
                                                control={form.control}
                                                name='startTimeOfMonth'
                                                render={({ field }) => (
                                                    <FormItem className='w-full flex-auto relative'>
                                                        <FormControl>
                                                            <FormatNumberVer2
                                                                decimalScale={0}
                                                                suffix='日'
                                                                placeholder={t('placeholder.start_of_month')}
                                                                statusDisplay='input'
                                                                thousandSeparator={true}
                                                                allowNegative={false}
                                                                value={field.value}
                                                                setvalueOfNumericFormat={(value) => {
                                                                    field.onChange(value);

                                                                    const endTimeVal = form.getValues('endTimeOfMonth');
                                                                    const startTimeVal = value;

                                                                    if (endTimeVal && startTimeVal && Number(endTimeVal) > Number(startTimeVal)) {
                                                                        form.clearErrors('endTimeOfMonth');
                                                                    }
                                                                    // form.trigger('endTimeOfMonth');
                                                                }}
                                                                disabled={isDeletedDisable}
                                                            />
                                                        </FormControl>
                                                        <FormMessage className='static xsm:absolute top-[50px] left-0' />
                                                    </FormItem>
                                                )}
                                            />
                                            <div className='h-12 flex items-center'>
                                                <ArrowRight className='p-1 size-6  rounded-full border xsm:block hidden' />
                                                <ArrowDown className='p-1 size-6  rounded-full border xsm:hidden' />
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name='endTimeOfMonth'
                                                render={({ field }) => (
                                                    <FormItem className='w-full flex-auto relative'>
                                                        <FormControl>
                                                            <FormatNumberVer2
                                                                decimalScale={0}
                                                                suffix='日'
                                                                placeholder={t('placeholder.end_of_month')}
                                                                statusDisplay='input'
                                                                thousandSeparator={true}
                                                                allowNegative={false}
                                                                value={field.value}
                                                                setvalueOfNumericFormat={(value) => {
                                                                    field.onChange(value);
                                                                    const startTimeVal = form.getValues('startTimeOfMonth');
                                                                    const endTimeVal = value;

                                                                    if (endTimeVal && startTimeVal && Number(endTimeVal) > Number(startTimeVal)) {
                                                                        form.clearErrors('startTimeOfMonth');
                                                                    }
                                                                }}
                                                                disabled={isDeletedDisable}
                                                            />
                                                        </FormControl>
                                                        <FormMessage className='static xsm:absolute top-[50px] left-0' />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                    {description && watchNonRentalType !== NON_RENTAL_TYPES.Day && (
                        <p>{description}</p>
                    )}
                </div>
                {isPage && (
                    <div className='point-600:mb-0 mb-24'></div>
                )}
                <div className={classNames(
                    'flex justify-center gap-4 p-4',
                    isPage && 'point-600:static point-600:w-full w-screen fixed left-0 bottom-0 point-600:py-0 py-3 point-600:bg-inherit bg-background-3 point-600:shadow-none shadow-shadow-3',
                    watchNonRentalType === NON_RENTAL_TYPES.Day && 'pt-0 pb-1',
                    watchNonRentalType === NON_RENTAL_TYPES.Monthly && !description && 'mt-2'
                )}
                >
                    {!isPage && (
                        <ButtonCus
                            onClick={handleClickCancel}
                            type='button'
                            variant='outline'
                            size='sm'
                            disabled={isDeletedDisable}
                        >
                            {t('button.cancel')}
                        </ButtonCus>
                    )}
                    {
                        isPage && bikeIds && bikeIds.length > 0 && !isOnlyView && (
                            <ButtonCusWithIcon
                                type='button'
                                variant='outline'
                                size='sm'
                                iconId='#icon-delete'
                                onClick={() => resetNonRetalDate()}
                                disabled={isDeletedDisable}
                            >
                                {t('button.reset_non_rental_date')}
                            </ButtonCusWithIcon>
                        )
                    }
                    {!isOnlyView && (
                        <ButtonCusWithIcon
                            type='submit'
                            variant='default'
                            size='sm'
                            iconId='#icon-update'
                            disabled={isDeletedDisable || form.formState.isSubmitting}
                        >
                            {t('button.update')}
                        </ButtonCusWithIcon>
                    )}
                </div>
            </form>
        </Form>
    );
});
